import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setStatus("error");
      setMessage("Lien invalide ou manquant.");
      return;
    }
    api
      .post("/auth/verify-email", { token })
      .then(() => {
        if (cancelled) return;
        setStatus("success");
        if (typeof refresh === "function") refresh();
        setTimeout(() => navigate(user ? "/account" : "/login"), 3000);
      })
      .catch((err) => {
        if (cancelled) return;
        const detail = err.response?.data?.detail;
        setStatus("error");
        setMessage(
          typeof detail === "string"
            ? detail
            : "Une erreur est survenue. Veuillez réessayer."
        );
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-16 bg-[#001F3F]"
      data-testid="verify-email-page"
    >
      <div className="w-full max-w-md text-center">
        {status === "loading" && (
          <div data-testid="verify-loading">
            <Loader2 size={36} strokeWidth={1.5} className="mx-auto text-white/60 animate-spin" />
            <h1 className="font-display mt-6 text-3xl font-bold uppercase tracking-tighter text-white">
              Vérification…
            </h1>
            <p className="mt-3 text-sm text-white/60">
              Confirmation de votre adresse email en cours.
            </p>
          </div>
        )}
        {status === "success" && (
          <div data-testid="verify-success">
            <CheckCircle2 size={36} strokeWidth={1.25} className="mx-auto text-emerald-400" />
            <h1 className="font-display mt-6 text-3xl font-bold uppercase tracking-tighter text-white">
              Email confirmé !
            </h1>
            <p className="mt-3 text-sm text-white/65">
              Votre compte est maintenant pleinement activé. Vous allez être redirigé…
            </p>
          </div>
        )}
        {status === "error" && (
          <div data-testid="verify-error">
            <XCircle size={36} strokeWidth={1.25} className="mx-auto text-[#FF6B6B]" />
            <h1 className="font-display mt-6 text-3xl font-bold uppercase tracking-tighter text-white">
              Lien invalide
            </h1>
            <p className="mt-3 text-sm text-white/65">{message}</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/account"
                className="inline-block border border-white/30 text-white hover:bg-white/10 transition uppercase tracking-[0.25em] text-xs font-bold px-6 py-3"
              >
                Mon compte
              </Link>
              <Link
                to="/login"
                className="inline-block border border-white bg-white text-[#001F3F] hover:bg-[#FFD700] hover:border-[#FFD700] transition uppercase tracking-[0.25em] text-xs font-bold px-6 py-3"
              >
                Se connecter
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
